import { flush, track } from '@/lib/analytics'
import { REPORT_SECTIONS, isSectionEnabled, resolveReportConfig, resolveSectionOrder } from '@/lib/reportConfig'
import { PLAN_SLOTS, visiblePlanKeys } from '@/stores/useStore'

function hasText(value) {
  return String(value || '').trim().length > 0
}

function lengthBucket(value) {
  const length = String(value || '').trim().length
  if (length === 0) return 'empty'
  if (length <= 20) return '1-20'
  if (length <= 60) return '21-60'
  if (length <= 140) return '61-140'
  return '141+'
}

function uniqueSectionKeys(sections) {
  return [...new Set((sections || []).map((section) => section?.key).filter(Boolean))]
}

export function reportConfigExportProps(config, {
  format = null,
  sections = REPORT_SECTIONS,
  includeSectionOrder = false,
} = {}) {
  const resolved = resolveReportConfig(config)
  const sectionKeys = uniqueSectionKeys(sections)
  const visibleSections = sectionKeys.filter((key) => isSectionEnabled(resolved, key, sections))
  const hiddenSections = sectionKeys.filter((key) => !isSectionEnabled(resolved, key, sections))
  const rawOrder = includeSectionOrder ? resolveSectionOrder(resolved) : sectionKeys
  const sectionOrder = rawOrder.filter((key) => sectionKeys.includes(key))
  const customOrderKeys = Array.isArray(resolved.sectionOrder)
    ? resolved.sectionOrder.filter((key) => sectionKeys.includes(key))
    : []

  return {
    ...(format ? { format } : {}),
    debrand: Boolean(resolved.debrand),
    hasLogo: Boolean(resolved.logoDataUrl),
    hasLogoSource: Boolean(resolved.logoSource),
    hasLogoCrop: Boolean(resolved.logoCrop),
    logoCropZoom: resolved.logoCrop?.zoom ? Math.round(Number(resolved.logoCrop.zoom) * 100) / 100 : null,
    titleSet: hasText(resolved.title),
    titleLength: lengthBucket(resolved.title),
    firmNameSet: hasText(resolved.firmName),
    firmNameLength: lengthBucket(resolved.firmName),
    clientNameSet: hasText(resolved.clientName),
    clientNameLength: lengthBucket(resolved.clientName),
    preparedBySet: hasText(resolved.preparedBy),
    preparedByLength: lengthBucket(resolved.preparedBy),
    coverFieldCount: ['title', 'firmName', 'clientName', 'preparedBy'].filter((key) => hasText(resolved[key])).length,
    sectionCount: sectionKeys.length,
    visibleSectionCount: visibleSections.length,
    hiddenSectionCount: hiddenSections.length,
    visibleSections,
    hiddenSections,
    sectionOrder,
    customSectionOrder: customOrderKeys.length > 0,
  }
}

export function cfoBriefExportProps(report, sections) {
  const sectionKeys = uniqueSectionKeys(sections)
  const visibleSections = sectionKeys.filter((key) => isSectionEnabled(report, key, sections))
  const hiddenSections = sectionKeys.filter((key) => !isSectionEnabled(report, key, sections))

  return {
    format: 'cfo_brief_html',
    cfoMode: true,
    companyNameSet: hasText(report?.companyName),
    companyNameLength: lengthBucket(report?.companyName),
    preparedBySet: hasText(report?.preparedBy),
    preparedByLength: lengthBucket(report?.preparedBy),
    sectionCount: sectionKeys.length,
    visibleSectionCount: visibleSections.length,
    hiddenSectionCount: hiddenSections.length,
    visibleSections,
    hiddenSections,
  }
}

export function workspaceExportProps(state, { cfoMode = false } = {}) {
  const shownPlanKeys = visiblePlanKeys(state)
  return {
    cfoMode: Boolean(cfoMode),
    employeeCount: state.employees?.length || 0,
    planCount: PLAN_SLOTS.filter((key) => state[key]).length,
    planKey: 'planA',
    shownPlanKeys,
    workspaceMode: state.workspaceMode,
  }
}

export function exportDownloadProps(state, format, {
  cfoMode = false,
  reportSections = null,
} = {}) {
  const props = {
    format,
    ...workspaceExportProps(state, { cfoMode }),
  }

  if (reportSections) {
    Object.assign(props, reportConfigExportProps(state.reportConfig, {
      format,
      sections: reportSections,
      includeSectionOrder: format === 'pdf',
    }))
  }

  return props
}

export function trackExportEvent(action, props = {}) {
  track(`export.${action}`, props)
  flush()
}
